import { FC, useEffect, useRef } from 'react'

import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'
import { Navigate } from 'react-router-dom'
import { Path } from 'constants/router'
import { SubscriptionPlan } from 'models/user'
import { logAnalyticsEvent } from 'utils/analytics'
import { usePricingRedirectTarget } from 'utils/hooks'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Redirects the legacy `/subscription/*` steps, which bkbn.com/pricing still links to, at whichever
 * page can actually serve the visitor (QA-246). Mounted only on those routes: the target hook waits
 * on the workspace selection with a timer and reports a timed-out wait to Sentry.
 */
export const PricingRedirect: FC = () => {
  const { path, isMissingPlanPermission } = usePricingRedirectTarget()
  const { baseUserData, organizationData } = useUserData()

  /**
   * `Enter Subscription Flow` marks entry into the paid funnel. The legacy step and the workspace
   * creation page each fire it on mount, so before QA-246 every arrival was counted — routing admins
   * to the Plan page, which fires nothing, would have dropped their share while their conversions
   * kept landing. Only the Plan destination is logged here; the creation page still logs its own.
   */
  const enterLogged = useRef<boolean>(false)
  useEffect(() => {
    if (path !== Path.WORKSPACE_SETTINGS_PLAN || enterLogged.current) return

    enterLogged.current = true
    logAnalyticsEvent('Enter Subscription Flow', {
      userEmail: baseUserData?.email,
      organizationId: organizationData?.id,
      planType: SubscriptionPlan.PRO_PER_SEAT,
      // The three entry paths into this funnel are otherwise indistinguishable in the event stream
      destination: 'workspace-plan',
    })
  }, [path, baseUserData?.email, organizationData?.id])

  if (!path) return <LoadingPage />

  // Both destinations are ordinary pages that this arrival changes: the creation page explains why
  // the user is there, and the Plan page offers a second workspace to someone who came to buy and
  // already pays for this one. Neither belongs to a visit made from inside the app.
  const state = path === Path.WORKSPACE_SETTINGS_PLAN
    ? { fromPricingRedirect: true }
    : isMissingPlanPermission ? { missingPlanPermission: true } : undefined

  return <Navigate to={path} replace state={state} />
}
